import { useEffect, useState } from 'react';
import { Button, InputNumber, Space, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/**
 * Panel lọc dùng trong `filterDropdown` trên header cột — CHUẨN cho mọi list page.
 *
 * Panel giữ state CỤC BỘ và chỉ đẩy lên trên khi bấm "Tìm": nhờ vậy gõ phím không
 * làm trang cha re-render, và mảng cột memo được.
 */

const BTN_WIDTH = 90;

/**
 * Phân cách nghìn là bắt buộc với tiền: gõ 500000 mà không thấy 500.000 thì thừa hoặc
 * thiếu một số 0 cũng không ai nhận ra.
 */
export const amountFormatter = (v?: string | number): string =>
  v === undefined || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Bỏ dấu phân cách, trả về CHUỖI — antd tự đổi sang số.
 *
 * Ô rỗng phải trả '' chứ KHÔNG phải NaN: antd coi NaN là giá trị không hợp lệ nên nó
 * từ chối thay đổi và khôi phục lại giá trị hợp lệ cuối cùng khi blur, đồng thời không
 * bắn onChange(null) — biểu hiện là "xoá số xong click ra ngoài thì số cũ quay lại".
 */
export const amountParser = (v?: string): string => {
  // Giữ dấu trừ ở ĐẦU: nếu bỏ hết ký tự không phải chữ số thì cột số âm (chênh lệch,
  // điều chỉnh kho) không bao giờ gõ được số âm, và prop `min` trở thành vô nghĩa.
  // Cột nào không cho âm thì truyền min={0}, antd tự chặn.
  const raw = (v ?? '').replace(/[^\d-]/g, '');
  const digits = raw.replace(/-/g, '');
  if (!digits) return '';
  return raw.startsWith('-') ? `-${digits}` : digits;
};

/** Giá trị antd trả về có thể là số, chuỗi, hoặc null khi ô trống. */
function toAmount(v: string | number | null): number | undefined {
  if (v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function ColumnFilterActions({
  onSearch,
  onReset,
  searchDisabled,
}: {
  onSearch: () => void;
  onReset: () => void;
  /** Chặn "Tìm" khi điều kiện không hợp lệ. "Xoá lọc" luôn bật — phải luôn có đường thoát. */
  searchDisabled?: boolean;
}) {
  return (
    <Space>
      <Button
        type="primary"
        size="small"
        icon={<SearchOutlined />}
        style={{ width: BTN_WIDTH }}
        disabled={searchDisabled}
        onClick={onSearch}
      >
        Tìm
      </Button>
      <Button size="small" style={{ width: BTN_WIDTH }} onClick={onReset}>
        Xoá lọc
      </Button>
    </Space>
  );
}

interface NumberRangeFilterPanelProps {
  from?: number;
  to?: number;
  onApply: (range: { from?: number; to?: number }) => void;
  close: () => void;
  /** Nhãn cho aria-label, vd 'Tổng tiền' → "Tổng tiền từ" / "Tổng tiền đến". */
  label: string;
  suffix?: string;
  /** Bỏ trống = cho phép số âm. Cột tiền truyền 0. */
  min?: number;
}

/**
 * Lọc theo KHOẢNG số. Cố ý KHÔNG dùng dropdown toán tử kiểu AMIS
 * (Bằng / Khác / Lớn hơn / …): với số liên tục thì "Bằng" và "Khác" gần như vô dụng,
 * còn ≥ / ≤ / trong-khoảng đều diễn đạt được bằng hai ô này mà người dùng không phải
 * mở dropdown lần nào — điền ô đầu = "từ trở lên", ô sau = "trở xuống", cả hai =
 * "trong khoảng".
 */
export function NumberRangeFilterPanel({
  from,
  to,
  onApply,
  close,
  label,
  suffix,
  min: minAllowed,
}: NumberRangeFilterPanelProps) {
  const { token } = theme.useToken();
  const [min, setMin] = useState<number | undefined>(from);
  const [max, setMax] = useState<number | undefined>(to);
  useEffect(() => setMin(from), [from]);
  useEffect(() => setMax(to), [to]);

  // Khoảng ngược (từ > đến) luôn trả 0 dòng. Báo rõ thay vì để người dùng nhìn bảng
  // trống mà không hiểu vì sao; cũng KHÔNG tự kẹp hay hoán đổi giá trị vì như vậy là
  // âm thầm sửa con số họ vừa gõ.
  const invalidRange = min !== undefined && max !== undefined && min > max;

  const apply = (nextMin?: number, nextMax?: number) => {
    if (nextMin !== undefined && nextMax !== undefined && nextMin > nextMax) return;
    onApply({ from: nextMin, to: nextMax });
    close();
  };

  const numberProps = {
    formatter: amountFormatter,
    parser: amountParser,
    min: minAllowed,
    controls: false,
    suffix,
  } as const;

  return (
    <div
      // Chốt bề rộng để 2 ô đủ chỗ nằm cùng hàng (dropdown của antd co theo nội dung,
      // để tự do thì nó chỉ ~204px và 2 ô bị rớt xuống 2 dòng). maxWidth theo viewport
      // để màn hẹp vẫn không tràn.
      style={{ padding: 8, width: 264, maxWidth: 'calc(100vw - 32px)' }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {/* display:flex, KHÔNG dùng <Space>: Space là inline-flex nên nó và hàng nút bên
          dưới sẽ nằm cùng một dòng thay vì xuống dòng. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <InputNumber
          {...numberProps}
          aria-label={`${label} từ`}
          placeholder="Từ"
          status={invalidRange ? 'error' : undefined}
          style={{ flex: '1 1 0', minWidth: 0 }}
          value={min}
          onChange={(v) => setMin(toAmount(v))}
          onPressEnter={() => apply(min, max)}
        />
        <InputNumber
          {...numberProps}
          aria-label={`${label} đến`}
          placeholder="Đến"
          status={invalidRange ? 'error' : undefined}
          style={{ flex: '1 1 0', minWidth: 0 }}
          value={max}
          onChange={(v) => setMax(toAmount(v))}
          onPressEnter={() => apply(min, max)}
        />
      </div>
      {/* Mô tả HIỆN TRẠNG đang sai, không phát biểu quy tắc ("phải nhỏ hơn hoặc bằng").
          Hai ô đã viền đỏ sẵn nên câu chữ chỉ cần gọi tên chỗ lệch. */}
      {invalidRange && (
        <div role="alert" style={{ marginBottom: 8, fontSize: 12, color: token.colorError }}>
          Ô &ldquo;Từ&rdquo; đang lớn hơn ô &ldquo;Đến&rdquo;
        </div>
      )}
      <ColumnFilterActions
        searchDisabled={invalidRange}
        onSearch={() => apply(min, max)}
        onReset={() => {
          setMin(undefined);
          setMax(undefined);
          apply(undefined, undefined);
        }}
      />
    </div>
  );
}
